/**
 * Admin Photo Approval Page
 *
 * Allows super admins to review and approve/reject photos
 * uploaded by members before they appear in the public gallery.
 *
 * Features:
 * - View pending photos queue
 * - Approve or reject photos
 * - View photo details and metadata
 * - Pagination for large queues
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router';
import { useAuth, useIsSuperAdmin } from '@/hooks/useAuth';
import { photosApi, ApiClientError } from '@/services/api';
import type { Photo, PaginationMeta } from '@/types';

/**
 * Photo card component for the approval queue
 */
interface PhotoCardProps {
  photo: Photo;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  isProcessing: boolean;
}

function PhotoCard({ photo, onApprove, onReject, isProcessing }: PhotoCardProps) {
  const [imageError, setImageError] = useState(false);

  // Format file size for display
  const formatFileSize = (bytes: number | null): string => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="card overflow-hidden">
      {/* Photo Preview */}
      <div className="relative aspect-video bg-black/20 overflow-hidden">
        {!imageError ? (
          <img
            src={photo.thumbnailUrl || photo.blobUrl}
            alt={photo.originalFilename || 'Uploaded photo'}
            className="w-full h-full object-contain"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-white/40">
            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        {/* Pending Badge */}
        <div className="absolute top-2 right-2">
          <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
            Pending
          </span>
        </div>
      </div>

      {/* Photo Details */}
      <div className="p-4">
        {/* Filename */}
        <h3 className="font-medium text-white truncate" title={photo.originalFilename || 'Untitled'}>
          {photo.originalFilename || 'Untitled'}
        </h3>

        {/* Metadata */}
        <div className="mt-2 space-y-1 text-sm text-white/60">
          <p>
            <span className="text-white/40">Uploaded by:</span>{' '}
            {photo.uploaderName || 'Unknown'}
          </p>
          <p>
            <span className="text-white/40">Size:</span>{' '}
            {formatFileSize(photo.fileSize)}
            {photo.width && photo.height && (
              <span className="text-white/40">
                {' '}• {photo.width}×{photo.height}
              </span>
            )}
          </p>
          <p>
            <span className="text-white/40">Uploaded:</span>{' '}
            {formatDate(photo.createdAt)}
          </p>
        </div>

        {/* Tags */}
        {photo.tags && photo.tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {photo.tags.map((tag) => (
              <span
                key={tag.tag}
                className="px-2 py-0.5 text-xs bg-white/10 text-white/70 rounded"
              >
                {tag.tag}
              </span>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => onApprove(photo.id)}
            disabled={isProcessing}
            className="flex-1 btn-primary text-sm py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <LoadingSpinner size="sm" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            Approve
          </button>
          <button
            type="button"
            onClick={() => onReject(photo.id)}
            disabled={isProcessing}
            className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg text-sm py-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <LoadingSpinner size="sm" />
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            Reject
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Loading spinner component
 */
function LoadingSpinner({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'h-4 w-4' : 'h-8 w-8';

  return (
    <svg className={`animate-spin ${sizeClasses} text-primary`} viewBox="0 0 24 24">
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

/**
 * Empty state component
 */
function EmptyState() {
  return (
    <div className="text-center py-16">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">All caught up!</h3>
      <p className="text-white/60">There are no photos waiting for approval.</p>
    </div>
  );
}

/**
 * Pagination component
 */
interface PaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
}

function Pagination({ pagination, onPageChange }: PaginationProps) {
  if (pagination.totalPages <= 1) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-4 mt-8">
      <button
        type="button"
        onClick={() => onPageChange(pagination.page - 1)}
        disabled={!pagination.hasPrev}
        className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>

      <span className="text-white/60">
        Page {pagination.page} of {pagination.totalPages}
      </span>

      <button
        type="button"
        onClick={() => onPageChange(pagination.page + 1)}
        disabled={!pagination.hasNext}
        className="btn-secondary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Admin Photo Approval Page Component
 */
export function PhotoApprovalPage() {
  const navigate = useNavigate();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const isSuperAdmin = useIsSuperAdmin();

  // State
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);

  const PHOTOS_PER_PAGE = 12;

  /**
   * Fetch pending photos
   */
  const fetchPendingPhotos = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await photosApi.getPending({
        page: currentPage,
        limit: PHOTOS_PER_PAGE,
      });

      setPhotos(response.data);
      setPagination(response.pagination);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to load pending photos';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage]);

  /**
   * Initial load
   */
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchPendingPhotos();
    }
  }, [authLoading, isAuthenticated, fetchPendingPhotos]);

  /**
   * Redirect non-admin users
   */
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isSuperAdmin)) {
      navigate('/login', { state: { from: '/admin/photos' }, replace: true });
    }
  }, [authLoading, isAuthenticated, isSuperAdmin, navigate]);

  /**
   * Handle photo approval
   */
  const handleApprove = useCallback(async (photoId: string) => {
    try {
      setProcessingIds((prev) => new Set(prev).add(photoId));
      setError(null);

      await photosApi.approve(photoId);

      // Remove from list
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));

      // Update pagination count
      if (pagination) {
        setPagination((prev) => prev ? {
          ...prev,
          total: prev.total - 1,
          totalPages: Math.ceil((prev.total - 1) / PHOTOS_PER_PAGE),
        } : null);
      }

      setSuccessMessage('Photo approved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to approve photo';
      setError(errorMessage);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(photoId);
        return next;
      });
    }
  }, [pagination]);

  /**
   * Handle photo rejection
   */
  const handleReject = useCallback(async (photoId: string) => {
    // Confirm rejection
    const confirmed = window.confirm(
      'Are you sure you want to reject this photo? This action cannot be undone.'
    );
    if (!confirmed) return;

    try {
      setProcessingIds((prev) => new Set(prev).add(photoId));
      setError(null);

      await photosApi.reject(photoId);

      // Remove from list
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));

      // Update pagination count
      if (pagination) {
        setPagination((prev) => prev ? {
          ...prev,
          total: prev.total - 1,
          totalPages: Math.ceil((prev.total - 1) / PHOTOS_PER_PAGE),
        } : null);
      }

      setSuccessMessage('Photo rejected.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof ApiClientError
        ? err.message
        : 'Failed to reject photo';
      setError(errorMessage);
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(photoId);
        return next;
      });
    }
  }, [pagination]);

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="container mx-auto px-4 py-16">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Don't render anything if not authorized (will redirect)
  if (!isAuthenticated || !isSuperAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">
            <span className="text-gradient">Photo Approval</span>
          </h1>
          <p className="text-white/60 mt-1">
            Review and approve member photo uploads
          </p>
        </div>
        <Link to="/admin" className="btn-secondary">
          Back to Admin
        </Link>
      </div>

      {/* Stats Bar */}
      {pagination && (
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <span className="text-white/80">
                <strong className="text-white">{pagination.total}</strong> photos pending review
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {/* Success Banner */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3">
          <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-green-400">{successMessage}</p>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : photos.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          {/* Photo Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {photos.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                onApprove={handleApprove}
                onReject={handleReject}
                isProcessing={processingIds.has(photo.id)}
              />
            ))}
          </div>

          {/* Pagination */}
          {pagination && (
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  );
}

export default PhotoApprovalPage;
